import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaClock,
  FaPaperPlane,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { messageAPI } from "../services/postgresService";
import { useSettings } from "../hooks/useSettings";

const ContactSection = () => {
  const { theme } = useSettings();
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    // Background image is now handled by useTheme hook
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = true;
    if (!formData.email.trim() || !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
      newErrors.email = true;
    }
    if (!formData.message.trim()) newErrors.message = true;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: false,
      }));
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // Use PostgreSQL messageAPI to save contact message
      await messageAPI.add({
        name: formData.name,
        email: formData.email,
        subject: formData.subject || "Contact Form Submission",
        message: formData.message,
      });

      setNotification({
        type: "success",
        message: t('contact.success'),
      });
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error("Error sending message:", error);
      setNotification({
        type: "error",
        message: t('contact.error'),
      });
    } finally {
      setIsSubmitting(false);
    }
    setTimeout(() => setNotification(null), 4000);
  };

  return (
    <div
      className="py-20 relative"
      style={{
        backgroundImage: `linear-gradient(135deg, rgba(31, 125, 83, 0.12) 0%, rgba(37, 95, 56, 0.12) 100%), linear-gradient(rgba(24, 35, 15, 0.75), rgba(24, 35, 15, 0.75)), url(${theme.coverImages.contact})`,
        backgroundAttachment: "fixed",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      id="contact"
    >
      <div className="container mx-auto px-4" ref={sectionRef}>
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('contact.title')}
          </h2>
          <motion.div
            className="w-24 h-1 bg-safir-primary mx-auto mb-6"
            initial={{ width: 0 }}
            whileInView={{ width: 96 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          ></motion.div>
          <p className="max-w-2xl mx-auto text-white">
            {t('contact.description')}
          </p>
        </motion.div>

        {notification && (
          <div
            className={`mb-6 text-center px-4 py-3 rounded-md font-semibold ${
              notification.type === "success"
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-700"
            }`}
          >
            {notification.message}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="bg-safir-dark text-white p-8 rounded-xl">
              <h3 className="text-2xl font-semibold mb-6">
                {t('contact.contactInfo')}
              </h3>

              <div className="space-y-6">
                <motion.div
                  className="flex items-start gap-4"
                  whileHover={{ x: 5 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <div className="bg-safir-primary p-3 rounded-full flex-shrink-0">
                    <FaMapMarkerAlt className="text-white" size={20} />
                  </div>
                  <div className="flex-grow min-w-0">
                    <h4 className="text-xl font-medium">{t('contact.ourLocation')}</h4>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      {t('contact.address')}
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  className="flex items-start gap-4"
                  whileHover={{ x: 5 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <div className="bg-safir-primary p-3 rounded-full flex-shrink-0">
                    <FaPhone className="text-white" size={20} />
                  </div>
                  <div className="flex-grow min-w-0">
                    <h4 className="text-xl font-medium">{t('contact.phoneNumber')}</h4>
                    <p
                      className="text-gray-300 text-sm text-left"
                      dir="ltr"
                      style={{ unicodeBidi: "plaintext" }}
                    >
                      {t('contact.phoneValue')}
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  className="flex items-start gap-4"
                  whileHover={{ x: 5 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <div className="bg-safir-primary p-3 rounded-full flex-shrink-0">
                    <FaEnvelope className="text-white" size={20} />
                  </div>
                  <div className="flex-grow min-w-0">
                    <h4 className="text-xl font-medium">{t('contact.emailAddress')}</h4>
                    <p className="text-gray-300 text-sm break-all">{t('contact.emailValue')}</p>
                  </div>
                </motion.div>

                <motion.div
                  className="flex items-start gap-4"
                  whileHover={{ x: 5 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <div className="bg-safir-primary p-3 rounded-full flex-shrink-0">
                    <FaClock className="text-white" size={20} />
                  </div>
                  <div className="flex-grow min-w-0">
                    <h4 className="text-xl font-medium">{t('contact.officeHours')}</h4>
                    <p className="text-gray-300 text-sm">{t('contact.officeHoursTime')}</p>
                  </div>
                </motion.div>
              </div>

              {/* Map */}
              <motion.div
                className="mt-8 h-64 bg-gray-200 rounded-lg overflow-hidden relative"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3261.847!2d69.0830009!3d34.502329!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x38d16756d753206b%3A0xac38e5db4c2da488!2sSAFIR-the%20Elite%20educational%20center!5e0!3m2!1sen!2s!4v1709123456789!5m2!1sen!2s"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allow="geolocation; fullscreen"
                  title="SAFIR-the Elite educational center location"
                ></iframe>
              </motion.div>
              <a
                href="https://www.google.com/maps/place/SAFIR-the+Elite+educational+center/@34.5021781,69.0829399,18.79z/data=!4m5!3m4!1s0x38d16756d753206b:0xac38e5db4c2da488!8m2!3d34.502329!4d69.0830009?entry=ttu&g_ep=EgoyMDI2MDIyNS4wIKXMDSoASAFQAw%3D%3D"
                target="_blank"
                rel="noopener noreferrer"
                className="text-safir-primary text-sm mt-2 inline-block hover:underline"
              >
                View on Google Maps →
              </a>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <motion.div
              className="bg-gray-50 p-8 rounded-xl shadow-lg"
              whileHover={{
                boxShadow:
                  "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
              }}
              transition={{ duration: 0.3 }}
            >
              <h3 className="text-2xl font-semibold text-safir-dark mb-6">
                {t('contact.sendMessage')}
              </h3>

              <form onSubmit={onSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-gray-700 mb-2">
                      {t('contact.fullNameRequired')}
                    </label>
                    <motion.input
                      type="text"
                      name="name"
                      className={`w-full p-3 border rounded-md focus:ring-safir-primary focus:border-safir-primary ${
                        errors.name ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder={t('contact.placeholder.name')}
                      value={formData.name}
                      onChange={handleInputChange}
                      whileFocus={{
                        scale: 1.01,
                        boxShadow: "0 0 0 3px rgba(31, 125, 83, 0.2)",
                      }}
                    />
                    {errors.name && (
                      <span className="text-red-500 text-sm mt-1">
                        {t('contact.nameRequired')}
                      </span>
                    )}
                  </div>

                  <div>
                    <label className="block text-gray-700 mb-2">
                      {t('contact.emailRequired')}
                    </label>
                    <motion.input
                      type="email"
                      name="email"
                      className={`w-full p-3 border rounded-md focus:ring-safir-primary focus:border-safir-primary ${
                        errors.email ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder={t('contact.placeholder.email')}
                      value={formData.email}
                      onChange={handleInputChange}
                      whileFocus={{
                        scale: 1.01,
                        boxShadow: "0 0 0 3px rgba(31, 125, 83, 0.2)",
                      }}
                    />
                    {errors.email && (
                      <span className="text-red-500 text-sm mt-1">
                        {t('contact.emailInvalid')}
                      </span>
                    )}
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-gray-700 mb-2">{t('contact.subject')}</label>
                  <motion.input
                    type="text"
                    name="subject"
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-safir-primary focus:border-safir-primary"
                    placeholder={t('contact.placeholder.subject')}
                    value={formData.subject}
                    onChange={handleInputChange}
                    whileFocus={{
                      scale: 1.01,
                      boxShadow: "0 0 0 3px rgba(31, 125, 83, 0.2)",
                    }}
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-gray-700 mb-2">
                    {t('contact.messageRequired')}
                  </label>
                  <motion.textarea
                    name="message"
                    className={`w-full p-3 border rounded-md focus:ring-safir-primary focus:border-safir-primary ${
                      errors.message ? "border-red-500" : "border-gray-300"
                    }`}
                    rows="5"
                    placeholder={t('contact.placeholder.message')}
                    value={formData.message}
                    onChange={handleInputChange}
                    whileFocus={{
                      scale: 1.01,
                      boxShadow: "0 0 0 3px rgba(31, 125, 83, 0.2)",
                    }}
                  ></motion.textarea>
                  {errors.message && (
                    <span className="text-red-500 text-sm mt-1">
                      {t('contact.messageRequired2')}
                    </span>
                  )}
                </div>

                <motion.button
                  type="submit"
                  className="w-full bg-safir-primary hover:bg-safir-secondary text-white font-semibold py-3 px-4 rounded-md transition-colors duration-300 flex items-center justify-center"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    t('contact.sending')
                  ) : (
                    <>
                      <FaPaperPlane className="mr-2" />
                      {t('contact.submit')}
                    </>
                  )}
                </motion.button>
              </form>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContactSection;
